import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeBlock from './CodeBlock';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  // User messages — right side, compact bubble
  userRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userMessage: {
    maxWidth: '85%',
    padding: '12px 16px',
    borderRadius: '12px 12px 2px 12px',
    background: '#E8F1FF',
    border: '1px solid #BBD4FF',
  },
  // AI messages — left side, full width
  aiRow: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  aiMessage: {
    width: '100%',
    padding: '16px 20px',
    borderRadius: '12px',
    background: '#fff',
    border: '1px solid #E8EBF2',
    boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
  },
  aiStreamingMessage: {
    width: '100%',
    padding: '16px 20px',
    borderRadius: '12px',
    background: '#fff',
    border: '1px solid #BBD4FF',
    boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  name: {
    fontWeight: '600',
    fontSize: '13px',
  },
  timestamp: {
    fontSize: '11px',
    color: '#9CA3AF',
  },
  content: {
    lineHeight: '1.7',
    wordBreak: 'break-word',
    color: '#374151',
  },
  userContent: {
    lineHeight: '1.5',
    wordBreak: 'break-word',
    color: '#1F2937',
  },
  image: {
    maxWidth: 'min(100%, 360px)',
    maxHeight: '300px',
    borderRadius: '8px',
    border: '1px solid #E8EBF2',
    marginBottom: '8px',
  },
  cursor: {
    display: 'inline-block',
    width: '2px',
    height: '1em',
    background: '#7C2BD0',
    marginLeft: '2px',
    animation: 'blink 0.8s infinite',
    verticalAlign: 'text-bottom',
  },
  empty: {
    textAlign: 'center',
    color: '#9CA3AF',
    padding: '60px 20px',
    fontSize: '15px',
  },
  aiIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#22C55E',
    display: 'inline-block',
  },
};

function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// AI answers are identified by the LN logo (falls back to the dot + "Lavan AI" label)
function AiName() {
  const [err, setErr] = React.useState(false);
  if (err) {
    return <span style={styles.aiIcon}><span style={styles.dot} /> Lavan AI</span>;
  }
  return (
    <img
      src={`${process.env.PUBLIC_URL}/logo.png`}
      alt="Lavan AI"
      onError={() => setErr(true)}
      style={{ height: 22, width: 22, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
    />
  );
}

export default function ChatDisplay({ messages = [], zoomLevel = 100 }) {
  if (messages.length === 0) {
    return (
      <div style={styles.empty}>
        <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
        Interview session ready. Start recording and send the first transcript.
      </div>
    );
  }

  const fontSize = `${Math.round(16 * zoomLevel / 100)}px`;

  return (
    <div style={styles.container}>
      <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
      {messages.map((msg, i) => {
        const isUser = msg.type === 'user';
        const isAI = msg.type === 'ai';
        // Stable key: timestamp+type avoids reconciliation bugs when the streaming
        // bubble (isStreaming:true) is replaced by the final message object
        const msgKey = msg.timestamp ? `${msg.type}-${msg.timestamp}-${i}` : `msg-${i}`;

        return (
          <div key={msgKey} style={isUser ? styles.userRow : styles.aiRow}>
            <div style={
              isUser
                ? styles.userMessage
                : (msg.isStreaming ? styles.aiStreamingMessage : styles.aiMessage)
            }>
              <div style={styles.header}>
                <span style={{ ...styles.name, color: isUser ? '#0866FF' : '#7C2BD0' }}>
                  {isUser ? (
                    'You'
                  ) : (
                    <AiName />
                  )}
                </span>
                <span style={styles.timestamp}>{formatTime(msg.timestamp)}</span>
              </div>

              {/* Show image if present */}
              {msg.imageUrl && (
                <img src={msg.imageUrl} alt="Uploaded" style={styles.image} />
              )}

              <div style={{ ...(isUser ? styles.userContent : styles.content), fontSize }}>
                {isAI ? (
                  <>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({ node, inline, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || '');
                          if (!inline && match) {
                            return <CodeBlock code={String(children).replace(/\n$/, '')} language={match[1]} />;
                          }
                          return (
                            <code
                              style={{
                                background: '#EEF1F6',
                                color: '#1F2937',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '0.9em',
                                fontFamily: "'JetBrains Mono', monospace",
                              }}
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        },
                        strong({ children }) {
                          return (
                            <strong style={{
                              color: '#5B21B6',
                              background: 'rgba(124, 43, 208, 0.10)',
                              borderRadius: '3px',
                              padding: '1px 4px',
                              fontWeight: '700',
                            }}>
                              {children}
                            </strong>
                          );
                        },
                        em({ children }) {
                          return (
                            <em style={{
                              color: '#2563EB',
                              fontStyle: 'normal',
                              background: 'rgba(8, 102, 255, 0.10)',
                              borderRadius: '3px',
                              padding: '1px 4px',
                            }}>
                              {children}
                            </em>
                          );
                        },
                        p({ children }) {
                          return <p style={{ marginBottom: '12px' }}>{children}</p>;
                        },
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                    {msg.isStreaming && <span style={styles.cursor} />}
                  </>
                ) : (
                  <span>{msg.content}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
